import { type FC, useEffect, useState } from 'react';

import AlertTriangle from '@crehana/compass.icons.outline/AlertTriangle';
import { boldFormatter, useTranslation } from '@crehana/i18n';
import { warning } from '@crehana/tailwindcss/colors';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { Container } from '@crehana/ui';
import { clsx } from 'clsx';

import { useExcelImporterStepperContext } from '@/views/AdminDashboard/components/ExcelImporterStepper/context';
import Stepper from '@/views/AdminDashboard/components/Stepper';

import { SIZE_ICON_MODERN_MODAL, WrapperIcon } from '../../../ModernModal';
import ModernModal from '../../../ModernModal/ModernModal';
import { getUniqueNestedEmails } from './utils';

const footerWording = [
  'FOOTER_STEP_UPLOAD_1',
  'FOOTER_STEP_UPLOAD_2',
  'FOOTER_STEP_UPLOAD_3',
];

interface FooterProps {
  show?: boolean;
}

const Footer: FC<FooterProps> = ({ show = true }) => {
  const { t } = useTranslation();

  const {
    currentStep,
    isNextButtonActive,
    handleNextStep,
    handlePreviousStep,
    validationMetadata,
    customConfirmSubmitModal,
    errors,
    isDark,
  } = useExcelImporterStepperContext();
  const [isPartialUploadConfirmModalOpen, setIsPartialUploadConfirmModalOpen] =
    useState(false);

  const handleToggleDialog = () => {
    setIsPartialUploadConfirmModalOpen(prevState => !prevState);
  };

  const handleConfirmDialog = () => {
    handleToggleDialog();
    handleNextStep();
  };

  const handlePrimaryAction = () => {
    if (
      currentStep === 2 &&
      validationMetadata?.totalValidRecords &&
      validationMetadata?.totalInvalidRecords
    ) {
      // biome-ignore lint/style/useBlockStatements: single-line early return
      if (customConfirmSubmitModal)
        return customConfirmSubmitModal.onAskConfirmation();
      // biome-ignore lint/style/useBlockStatements: single-line early return
      if (
        validationMetadata.totalValidRecords > 0 &&
        validationMetadata.totalInvalidRecords > 0
      )
        handleToggleDialog();
    } else {
      handleNextStep();
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (customConfirmSubmitModal?.isConfirmed) handleNextStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customConfirmSubmitModal?.isConfirmed]);

  const bgFooter = isDark
    ? 'bg-base-light-dark-mode'
    : 'tw-bg-neutral-light-100';
  const borderTop = clsx(
    'border-t border-solid',
    isDark ? 'border-base-lighter-24' : 'tw-border-neutral-light-400',
  );

  const stepLabels = Array(3)
    .fill('')
    .map((_, index) => t(footerWording[index]));

  const backLabel = t(currentStep > 1 ? 'FOOTER_BACK' : 'FOOTER_CANCEL');

  if (!show) {
    return null;
  }

  return (
    <div
      className={clsx(bgFooter, borderTop, 'tw-flex tw-items-center tw-w-full')}
    >
      <Container>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-w-full tw-px-2 tw-pt-3.2 tw-pb-4">
          <div className="order-2 md:order-1">
            <button
              type="button"
              className="tw-btn-tertiary-s"
              onClick={handlePreviousStep}
            >
              {backLabel}
            </button>
          </div>
          <div className="flex items-center justify-center flex-grow order-1 md:order-2">
            <Stepper
              isDark={isDark}
              steps={stepLabels}
              currentStep={currentStep}
            />
          </div>
          <button
            type="button"
            className={clsx(
              'tw-btn-primary-s order-3 md:order-3',
              !isNextButtonActive && 'tw-btn-disabled',
            )}
            onClick={handlePrimaryAction}
            disabled={!isNextButtonActive}
          >
            {t('FOOTER_NEXT')}
          </button>
        </div>
      </Container>
      {/* //TODO: This should be configurable from context */}
      <ModernModal
        centeredButtons
        isOpen={isPartialUploadConfirmModalOpen}
        labelConfirmButton={t('GLOBAL_CONTINUE')}
        labelCloseButton={t('GLOBAL_CANCEL')}
        onClose={handleToggleDialog}
        onConfirm={handleConfirmDialog}
      >
        <WrapperIcon
          className="mx-auto mt-24"
          type={SIZE_ICON_MODERN_MODAL.M}
          customColor={warning}
        >
          <AlertTriangle size={28} className="tw-text-warning-600" />
        </WrapperIcon>
        <p className="w-full tw-font-h5 tw-font-semibold mt-24 mx-auto mb-12 dark:tw-text-neutral-light-100 text-center">
          {t('CONFIRM_PARTIAL_SUBMIT_TITLE')}
        </p>
        <p className="w-full tw-font-body4 mx-auto mb-12 dark:tw-text-neutral-light-100 text-center">
          {t('CONFIRM_PARTIAL_SUBMIT_DESCRIPTION', {
            formatter: boldFormatter,
            interpolation: {
              count: getUniqueNestedEmails(errors).length || 0,
            },
          })}
        </p>
      </ModernModal>
      {customConfirmSubmitModal && customConfirmSubmitModal.component}
    </div>
  );
};

export default Footer;
